import React, { useState } from "react";
import { Modal, Form, Input, Upload, Button, App } from "antd";
import { PaperClipOutlined } from "@ant-design/icons";
import type { UploadFile } from "antd";
import { useCreate } from "@refinedev/core";
import { useTranslation } from "react-i18next";

interface AddCommentModalProps {
  visible: boolean;
  onCancel: () => void;
  incidenceId: number;
  onSuccess?: () => void;
}

/**
 * AddCommentModal Component
 * Modal for adding a new comment to an incidence with optional file attachment
 */
export const AddCommentModal: React.FC<AddCommentModalProps> = ({
  visible,
  onCancel,
  incidenceId,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const { message } = App.useApp();
  const { mutate: createComment } = useCreate();

  const handleSubmit = async (values: { message: string }) => {
    if (!values.message || values.message.trim() === "") {
      message.error(
        t(
          "incidence.comment_empty_error",
          "El comentario no puede estar vacío"
        )
      );
      return;
    }

    setSubmitting(true);

    try {
      // Prepare the data object
      const commentData: any = {
        incidence: incidenceId,
        message: values.message.trim(),
      };

      // Convert file to base64 if present
      if (fileList.length > 0 && fileList[0].originFileObj) {
        const file = fileList[0].originFileObj;
        
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => {
            const result = reader.result as string;
            // Remove the data:image/png;base64, or data:application/pdf;base64, prefix
            const base64String = result.split(',')[1];
            resolve(base64String);
          };
          reader.onerror = (error) => reject(error);
        });

        commentData.attachment_content = base64;
        commentData.attachment_filename = file.name;
      }

      createComment(
        {
          resource: "incidence-comments",
          values: commentData,
        },
        {
          onSuccess: () => {
            message.success(
              t("incidence.comment_added", "Comentario añadido exitosamente")
            );
            form.resetFields();
            setFileList([]);
            setSubmitting(false);
            onCancel(); // Close modal
            
            // Call onSuccess callback to refresh the list
            if (onSuccess) {
              onSuccess();
            }
          },
          onError: (error) => {
            message.error(
              t(
                "incidence.comment_error",
                "Error al añadir el comentario. Por favor, intente nuevamente."
              )
            );
            setSubmitting(false);
          },
        }
      );
    } catch (error) {
      message.error(
        t("incidence.file_read_error", "Error al leer el archivo")
      );
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setFileList([]);
    onCancel();
  };

  return (
    <Modal
      title={t("incidence.add_comment", "Añadir Comentario")}
      open={visible}
      onCancel={handleCancel}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          {t("common.cancel", "Cancelar")}
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={submitting}
          onClick={() => form.submit()}
        >
          {t("incidence.submit_comment", "Añadir Comentario")}
        </Button>,
      ]}
      width={600}
    >
      <Form form={form} onFinish={handleSubmit} layout="vertical">
        <Form.Item
          name="message"
          label={t("incidence.comment_label", "Comentario")}
          rules={[
            {
              required: true,
              message: t(
                "incidence.comment_required",
                "Por favor ingrese un comentario"
              ),
            },
            {
              whitespace: true,
              message: t(
                "incidence.comment_empty_error",
                "El comentario no puede estar vacío"
              ),
            },
            {
              min: 1,
              message: t(
                "incidence.comment_too_short",
                "El comentario debe tener al menos 1 carácter"
              ),
            },
          ]}
        >
          <Input.TextArea
            rows={4}
            placeholder={t(
              "incidence.comment_placeholder",
              "Escriba un comentario..."
            )}
            maxLength={2000}
            showCount
          />
        </Form.Item>

        <Form.Item
          label={t("incidence.attachment", "Archivo adjunto (opcional)")}
          help={t(
            "incidence.attachment_help",
            "Puede adjuntar imágenes (JPG, PNG, GIF) o documentos PDF. Tamaño máximo: 10MB"
          )}
        >
          <Upload
            fileList={fileList}
            beforeUpload={(file) => {
              // Validate file type
              const isValidType =
                file.type.startsWith("image/") ||
                file.type === "application/pdf";

              if (!isValidType) {
                message.error(
                  t(
                    "incidence.invalid_file_type",
                    "Solo se permiten imágenes (JPG, PNG, GIF) o archivos PDF"
                  )
                );
                return Upload.LIST_IGNORE;
              }

              // Validate file size (10MB)
              const isValidSize = file.size / 1024 / 1024 < 10;
              if (!isValidSize) {
                message.error(
                  t(
                    "incidence.file_too_large",
                    "El archivo debe ser menor a 10MB"
                  )
                );
                return Upload.LIST_IGNORE;
              }

              // Add file to list with originFileObj
              const uploadFile: UploadFile = {
                uid: file.uid,
                name: file.name,
                status: 'done',
                originFileObj: file,
              };
              setFileList([uploadFile]);
              
              return false; // Prevent auto upload
            }}
            onRemove={() => {
              setFileList([]);
            }}
            maxCount={1}
            accept="image/*,.pdf"
          >
            <Button icon={<PaperClipOutlined />}>
              {t("incidence.select_file", "Seleccionar archivo")}
            </Button>
          </Upload>
        </Form.Item>
      </Form>
    </Modal>
  );
};
